import Tooltip from 'apollo-react/components/Tooltip';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import NewComponent from './components/editor/components/NewComponent';
import ControlWrapper from './components/editor/ControlWrapper';
import {
  BUTTON_LIST,
  FORM_KEYS,
  FORM_MODEL_ATTRIBUTES,
  SS_CONTROL_TYPE,
} from './constants/form-manager.constants';
import { AI_SUMMARY_APP_SETTING_KEY } from './constants/runtime.constants';
import { FormContext, SubGridContext } from './context';
import getComponent from './getComponent';
import { getIsFormChangesMode, getNetworkStatus } from './selectors/common.selectors';
import {
  getEvaluatedConditions,
  getEvaluatedExpression,
  getIndexForControl,
  getIsControlAdded,
  getIsControlChanged,
  getModelById,
  getPropByModel,
  getTranslatedLabel,
  isHeaderComponent,
  isInHeaderComponent,
} from './selectors/form-manager.selectors';
import { getFlexValue } from './utils/common.utils';
import { equalityFn } from './utils/form-manager.utils';
import { useFormSelector } from '../custom-hooks';
import { useWindowDimensions } from '../custom-hooks/useWindowDimensions';
import { getAppSettingsByKey } from '../selectors/AppConfigsSelectors';
import { isTrue } from '../utils/commonUtils';

// handles generic controller actions
const DynamicField = ({
  id,
  forceRender,
  index: parentIndex = [0],
  classes,
  inTable: parentInTable,
  isParentViewOnly = false,
  isParentColumnHiddenFCM = false,
  isDetail,
  isInComplexGridRow,
  maxWidth = 568,
  width = '100%',
  ...rest
}) => {
  const { isMobileDevice } = useWindowDimensions();
  const { pathname } = useLocation();
  const formKey = useContext(FormContext);
  const inSubGrid = useContext(SubGridContext);
  // get component data from formModel on redux store
  const {
    childrenNodes = [],
    controlType,
    entityName,
    fieldName,
    parent,
    defaultLabel,
    ...modelProps
  } = useFormSelector(getModelById, { id });
  const parentType = useFormSelector(getPropByModel, {
    id: parent,
    prop: FORM_MODEL_ATTRIBUTES.CONTROL_TYPE,
  });
  const index = useFormSelector(getIndexForControl, { id, parentIndex }, equalityFn);
  const isChildAttachment = modelProps.gridType === 'ChildAttachment';
  const selectorProps = { id, index: isChildAttachment ? parentIndex : index, entityName };
  const isControlViewOnly = useFormSelector(getEvaluatedConditions, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.VIEW_ONLY,
  });
  const dataToolTip = useFormSelector(getEvaluatedExpression, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.DATA_TOOLTIP,
  });
  const isControlHidden = useFormSelector(getEvaluatedConditions, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.HIDDEN,
  });
  const label = useFormSelector(getEvaluatedExpression, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.LABEL,
  });
  const translatedLabel = useFormSelector(getTranslatedLabel, {
    entityName,
    fieldName,
    label,
  });
  const isHeader = useFormSelector(isHeaderComponent, { id, pathname });
  const isInHeader = useFormSelector(isInHeaderComponent, { id, pathname });
  const isFormChangesMode = useFormSelector(getIsFormChangesMode);
  const isControlAdded = useFormSelector(getIsControlAdded, { id });
  const isControlChanged = useFormSelector(getIsControlChanged, { id });

  const translationServiceEnabledFlag = useSelector(
    getAppSettingsByKey('AzureCognitiveTranslationService/Enabled')
  );
  const summarizationServiceEnabledFlag = useSelector(
    getAppSettingsByKey(AI_SUMMARY_APP_SETTING_KEY)
  );
  const isOnline = useSelector(getNetworkStatus);

  const isEditor = formKey === FORM_KEYS.EDITOR;
  const instanceId = `${id}#[${index.toString()}]`;
  const inTable = parentInTable || parentType === SS_CONTROL_TYPE.GRID_COLUMN;
  const inSubGridOrMobile = inSubGrid || isMobileDevice;
  const isAdvancedTextBox = controlType === SS_CONTROL_TYPE.ADVANCED_TEXT_BOX;

  const disableWidth =
    (inTable && controlType === SS_CONTROL_TYPE.TEXT_VIEW) || rest.inFieldGroup || isInHeader;

  const isPreviewOnly =
    isInHeader && isTrue(modelProps.previewOnly) && formKey === FORM_KEYS.DETAIL;

  const disableMaxWidth =
    isEditor ||
    (isAdvancedTextBox && !inSubGridOrMobile && !inTable) ||
    controlType === SS_CONTROL_TYPE.CHECK_BOX;

  const controlMaxWidth = disableMaxWidth
    ? null
    : rest.isControlAddedFCM || rest.isControlChangedFCM
    ? 618
    : maxWidth;

  const isViewOnly =
    isControlViewOnly ||
    isParentViewOnly ||
    (isInComplexGridRow &&
      ![SS_CONTROL_TYPE.MODAL, SS_CONTROL_TYPE.TEXT_AREA].includes(controlType));

  // map through childrenNodes and create an array of DynamicFields from it
  const children =
    ![SS_CONTROL_TYPE.GRID].includes(controlType) &&
    childrenNodes.map((childKey) => (
      <MemoizedField
        classes={classes}
        key={childKey}
        id={childKey}
        index={index}
        inTable={inTable}
        isParentViewOnly={isViewOnly}
        isInHeader={isInHeader || isHeader}
        isDetail={isDetail}
      />
    ));

  // declare the component props
  const props = {
    ...rest,
    ...modelProps,
    entityName,
    fieldName,
    childrenNodes,
    id,
    index,
    parentIndex,
    children,
    label: translatedLabel ?? defaultLabel,
    inTable,
    isDetail,
    isHeader,
    isInHeader,
    formKey,
    parentType,
    isViewOnly,
    parent,
    instanceId,
    translationServiceEnabledFlag,
    summarizationServiceEnabledFlag,
    isInComplexGridRow,
    isOnline,
    isControlHiddenFCM:
      ((isControlHidden || isPreviewOnly) && isFormChangesMode) || isParentColumnHiddenFCM, // FCM - Form Changes Mode
    isControlAddedFCM: isControlAdded && isFormChangesMode,
    isControlChangedFCM: isControlChanged && isFormChangesMode,
  };

  const component = dataToolTip ? (
    <Tooltip
      title={dataToolTip}
      placement="bottom-start"
      classes={{
        tooltip: {
          maxWidth: 400,
        },
      }}
    >
      <div
        style={{
          marginRight: inTable && 10,
          maxWidth: controlMaxWidth,
          width: disableWidth ? null : width,
          flex: getFlexValue(
            width,
            inTable,
            controlType,
            rest.inFieldGroup,
            isInHeader,
            parentType
          ),
        }}
      >
        {getComponent({ controlType, isEditor, ...props })}
      </div>
    </Tooltip>
  ) : (
    getComponent({ controlType, isEditor, ...props })
  );

  if (isEditor) {
    return (
      <ControlWrapper controlType={controlType} id={id} inTable={inTable}>
        {component}
        {[SS_CONTROL_TYPE.SUB_GRID].includes(controlType) && !childrenNodes.length && (
          <NewComponent controlParentId={id} previousControlId="" inTable={inTable} />
        )}
      </ControlWrapper>
    );
  }

  if ((isControlHidden || isPreviewOnly) && !isFormChangesMode) {
    return null;
  }

  // we only want to render buttons on FormFooters
  if ((BUTTON_LIST.includes(controlType) || isHeader) && !forceRender) {
    return null;
  }

  return component;
};

const MemoizedField = React.memo(DynamicField);

export default MemoizedField;
